import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Save, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { previewSegmentCount, saveSegment } from "../../../apis/contactSegments";
import { listCustomFields } from "../../../apis/contactCustomFields";

/**
 * Segment builder
 *
 * - Renders an AND/OR rule tree where each leaf is { field, op, value }.
 * - Debounces preview-count calls to the server while the user edits.
 * - "Create Contact List" button is enabled only when count > 0 and a
 *   name is provided. The dialog asks: static snapshot vs dynamic.
 */

const FIELD_GROUPS = [
  {
    label: "Identity",
    options: [
      { value: "full_name", label: "Full name" },
      { value: "first_name", label: "First name" },
      { value: "last_name", label: "Last name" },
      { value: "email", label: "Email" },
      { value: "company_name", label: "Company" },
      { value: "designation", label: "Designation" },
      { value: "city", label: "City" },
      { value: "state", label: "State" },
      { value: "country", label: "Country" },
      { value: "pincode", label: "Pincode" },
      { value: "gender", label: "Gender" },
      { value: "status", label: "Status" },
      { value: "source", label: "Source" },
      { value: "whatsapp_name", label: "WhatsApp name" },
    ],
  },
  {
    label: "Engagement",
    options: [
      { value: "engagement.replied_in_days", label: "Replied in last N days" },
      { value: "engagement.read_in_days", label: "Read in last N days" },
      { value: "engagement.last_inbound_in_days", label: "Inbound in last N days" },
      { value: "engagement.last_outbound_in_days", label: "Outbound in last N days" },
      { value: "engagement.never_engaged", label: "Never engaged" },
      { value: "engagement.has_unread", label: "Has unread" },
    ],
  },
  {
    label: "Tags",
    options: [{ value: "tags", label: "Tag" }],
  },
];

const STRING_OPS = [
  { value: "=", label: "equals" },
  { value: "!=", label: "not equals" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "doesn’t contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
];

const NUMBER_DAYS_OPS = [
  { value: "<=", label: "in last (≤ days)" },
  { value: ">", label: "more than (days ago)" },
];

const TAG_OPS = [
  { value: "contains", label: "has tag" },
  { value: "not_contains", label: "doesn’t have tag" },
  { value: "is_empty", label: "no tags" },
  { value: "is_not_empty", label: "has any tags" },
];

const BOOL_OPS = [{ value: "=", label: "is true" }];

function isEngagementDays(field) {
  return /_in_days$/.test(field || "");
}
function isEngagementBool(field) {
  return field === "engagement.never_engaged" || field === "engagement.has_unread";
}
function getOpsForField(field, customFields = []) {
  if (!field) return STRING_OPS;
  if (field === "tags") return TAG_OPS;
  if (isEngagementBool(field)) return BOOL_OPS;
  if (isEngagementDays(field)) return NUMBER_DAYS_OPS;
  if (field.startsWith("custom.")) {
    const key = field.slice("custom.".length);
    const cf = customFields.find((f) => f.field_key === key);
    if (cf?.field_type === "number") {
      return [
        { value: "=", label: "equals" },
        { value: "!=", label: "not equals" },
        { value: ">", label: ">" },
        { value: ">=", label: "≥" },
        { value: "<", label: "<" },
        { value: "<=", label: "≤" },
      ];
    }
  }
  return STRING_OPS;
}

const newLeaf = () => ({ field: "city", op: "=", value: "" });
const newGroup = () => ({ operator: "AND", conditions: [newLeaf()] });

const SegmentBuilder = ({ phoneId, onClose, onSaved }) => {
  const [rules, setRules] = useState(newGroup());
  const [count, setCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [customFields, setCustomFields] = useState([]);
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    listCustomFields()
      .then((r) => setCustomFields(r.data.fields || []))
      .catch(() => setCustomFields([]));
  }, []);

  // Debounced preview
  useEffect(() => {
    if (!phoneId) return;
    const t = setTimeout(() => runPreview(), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId, JSON.stringify(rules)]);

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError("");
    try {
      const r = await previewSegmentCount({ phone_id: phoneId, rules });
      setCount(r.data.count || 0);
    } catch (err) {
      setPreviewError(err.response?.data?.error || "Failed to preview");
      setCount(null);
    } finally {
      setPreviewing(false);
    }
  };

  const fieldOptions = useMemo(() => {
    const groups = [...FIELD_GROUPS];
    if (customFields.length > 0) {
      groups.push({
        label: "Custom",
        options: customFields.map((cf) => ({
          value: `custom.${cf.field_key}`,
          label: cf.label,
        })),
      });
    }
    return groups;
  }, [customFields]);

  const updateNode = (path, updater) => {
    setRules((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < path.length; i++) node = node.conditions[path[i]];
      updater(node);
      return next;
    });
  };

  const removeAt = (parentPath, idx) => {
    setRules((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < parentPath.length; i++) node = node.conditions[parentPath[i]];
      node.conditions.splice(idx, 1);
      return next;
    });
  };

  const addCondition = (path, kind = "leaf") => {
    setRules((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < path.length; i++) node = node.conditions[path[i]];
      node.conditions.push(kind === "group" ? newGroup() : newLeaf());
      return next;
    });
  };

  const Group = ({ node, path }) => (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center gap-2 mb-3">
        <select
          value={node.operator}
          onChange={(e) =>
            updateNode(path, (n) => { n.operator = e.target.value; })
          }
          className="text-xs font-semibold border border-gray-200 rounded px-2 py-1 bg-white"
        >
          <option value="AND">All of (AND)</option>
          <option value="OR">Any of (OR)</option>
        </select>
        <span className="text-xs text-gray-500">match every condition</span>
      </div>
      <div className="space-y-2">
        {node.conditions.map((child, idx) => {
          const childPath = [...path, idx];
          if (child.operator) {
            return (
              <div key={idx} className="flex items-start gap-2">
                <div className="flex-1"><Group node={child} path={childPath} /></div>
                <button
                  onClick={() => removeAt(path, idx)}
                  className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                  title="Remove group"
                ><Trash2 size={14} /></button>
              </div>
            );
          }
          return (
            <Leaf
              key={idx}
              leaf={child}
              fieldOptions={fieldOptions}
              customFields={customFields}
              onChange={(updater) => updateNode(childPath, updater)}
              onRemove={() => removeAt(path, idx)}
            />
          );
        })}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => addCondition(path, "leaf")}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50"
        >
          <Plus size={12} /> condition
        </button>
        <button
          onClick={() => addCondition(path, "group")}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50"
        >
          <Plus size={12} /> group
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Build a contact segment</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Combine identity, engagement, tags and custom field rules.
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <Group node={rules} path={[]} />
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm">
              <button
                onClick={runPreview}
                disabled={previewing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                <RefreshCw size={13} /> {previewing ? "Counting…" : "Recount"}
              </button>
              {previewError ? (
                <span className="text-rose-600">{previewError}</span>
              ) : (
                <span className="text-gray-700">
                  Matching contacts: <span className="font-semibold">{count === null ? "—" : count.toLocaleString()}</span>
                </span>
              )}
            </div>
            <button
              onClick={() => setShowSave(true)}
              disabled={!count || count <= 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
            >
              <Save size={14} /> Create Contact List
            </button>
          </div>
        </div>
      </div>

      {showSave && (
        <SaveSegmentDialog
          phoneId={phoneId}
          rules={rules}
          count={count}
          onClose={() => setShowSave(false)}
          onSaved={() => { setShowSave(false); onSaved(); }}
        />
      )}
    </>
  );
};

const Leaf = ({ leaf, fieldOptions, customFields, onChange, onRemove }) => {
  const ops = getOpsForField(leaf.field, customFields);
  const showValue = !["is_empty", "is_not_empty"].includes(leaf.op);
  const isDays = isEngagementDays(leaf.field);
  const isBool = isEngagementBool(leaf.field);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-2 flex flex-wrap items-center gap-2">
      <select
        value={leaf.field}
        onChange={(e) => onChange((n) => {
          n.field = e.target.value;
          // Reset op to a sensible default for the new field family.
          const newOps = getOpsForField(e.target.value, customFields);
          n.op = newOps[0]?.value || "=";
          n.value = "";
        })}
        className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white"
      >
        {fieldOptions.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </optgroup>
        ))}
      </select>

      <select
        value={leaf.op}
        onChange={(e) => onChange((n) => { n.op = e.target.value; })}
        className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white"
      >
        {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      {showValue && !isBool && (
        <input
          type={isDays ? "number" : "text"}
          value={leaf.value ?? ""}
          onChange={(e) => onChange((n) => { n.value = isDays ? Number(e.target.value) : e.target.value; })}
          placeholder={isDays ? "days" : "value"}
          className="text-sm border border-gray-200 rounded px-2 py-1.5 flex-1 min-w-[120px]"
        />
      )}
      {isBool && (
        <span className="text-xs text-gray-500 italic">no value</span>
      )}

      <button
        onClick={onRemove}
        className="ml-auto p-1 text-rose-500 hover:bg-rose-50 rounded"
        title="Remove"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const SaveSegmentDialog = ({ phoneId, rules, count, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "",
    description: "",
    materialize: true, // static snapshot by default
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const r = await saveSegment({
        phone_id: phoneId,
        name: form.name.trim(),
        description: form.description || null,
        rules,
        materialize: !!form.materialize,
      });
      toast.success(
        form.materialize
          ? `List created with ${r.data.members_added || 0} members`
          : "Dynamic list created"
      );
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Save as contact list</h3>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-gray-600">
              About to save a list with <span className="font-semibold">{count?.toLocaleString()}</span> matching contact(s).
            </p>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2"
                placeholder="e.g. Replied in last 25 days"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Description (optional)</span>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2"
              />
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 p-2 border border-gray-200 rounded-md cursor-pointer">
                <input
                  type="radio"
                  name="materialize"
                  checked={form.materialize === true}
                  onChange={() => setForm({ ...form, materialize: true })}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Static snapshot</p>
                  <p className="text-xs text-gray-500">Members captured now. Future contacts won’t be added unless you refresh.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 border border-gray-200 rounded-md cursor-pointer">
                <input
                  type="radio"
                  name="materialize"
                  checked={form.materialize === false}
                  onChange={() => setForm({ ...form, materialize: false })}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Dynamic (auto-refresh)</p>
                  <p className="text-xs text-gray-500">Rules are stored. Click <em>Refresh</em> on the list to materialise.</p>
                </div>
              </label>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Create list"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SegmentBuilder;
